"use client";

import { Button, Label } from "@wtc/ui";
import { X } from "lucide-react";
import { UserSelect } from "@/components/shared/UserSelect";
import type { User } from "@/lib/types/user";

interface MspDocumentSharingSectionProps {
  sharedWithUsers: string[];
  allUsers: User[] | undefined;
  onAddUser: (userId: string) => void | Promise<void>;
  onRemoveUser: (userId: string) => void;
}

function toUserSelectHandler(
  onAdd: (userId: string) => void | Promise<void>
): (userId: string) => Promise<void> {
  return async (userId: string) => {
    await onAdd(userId);
  };
}

export function MspDocumentSharingSection({
  sharedWithUsers,
  allUsers,
  onAddUser,
  onRemoveUser,
}: MspDocumentSharingSectionProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Share with Users</Label>
        <p className="text-sm text-muted-foreground">
          Select specific users to share this document with. These users will have access even if
          they don&apos;t have role-based access.
        </p>
        <UserSelect onUserSelect={toUserSelectHandler(onAddUser)} />
      </div>

      {sharedWithUsers.length > 0 && (
        <div className="space-y-2">
          <Label>Currently Shared With</Label>
          <div className="space-y-2">
            {sharedWithUsers.map((userId) => {
              const user = allUsers?.find((u) => u.id === userId);
              if (!user) return null;

              return (
                <div
                  key={userId}
                  className="flex items-center justify-between p-2 border rounded"
                >
                  <span className="text-sm">{user.email}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveUser(userId)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
